import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { ClinicalSubCategoryDetailsComponent } from './detail/clinical-sub-category-detail.component';
import { MatDialog } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { PaginationResult } from 'src/app/models/common.response';
import { debounceTime, distinctUntilChanged, finalize, Subject, switchMap, takeUntil } from 'rxjs';
import { ClinicalSubCategoryService } from './clicnical-sub-category.service';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { DeleteDialog } from '../../dialogs/delete.component';
import { LookupService } from 'src/app/services/lookup.service';
import { CommonDropDown } from 'src/app/common/types/dropdown.type';
import { ToastrService } from 'ngx-toastr';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AppBreadcrumbComponent } from "../../../layouts/full/shared/breadcrumb/breadcrumb.component";
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IClinicalSubCategory, IClinicalSubCategoryListRequest } from './iclinical-sub-category';
import { TranslationUtils } from 'src/app/common/utils/translation.utils';
import { PAGE_SIZE_OPTIONS } from 'src/app/common/constant/constants.type';


@Component({
  selector: 'app-clicnical-sub-category',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TablerIconsModule,
    AppBreadcrumbComponent,
    ReactiveFormsModule,
    RouterModule,
    TranslateModule
  ],
  templateUrl: './clicnical-sub-category.component.html',
  styleUrl: './clicnical-sub-category.component.scss'
})
export class ClicnicalSubCategoryComponent implements OnInit, AfterViewInit {

  dataSource = new MatTableDataSource<IClinicalSubCategory>();
  displayedColumns: string[] = [
    'name',
    'categorytype',
    'description',
    'action',
  ];
  pagination: PaginationResult;
  subcategorycount: number = 0;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  isLoading: boolean = false;
  subcategorydata: IClinicalSubCategory[];
  categoryList: CommonDropDown[];
  categoryId: string | null;
  selectedcategory: CommonDropDown | undefined;
  searchSubcategory: FormControl = new FormControl();
  pageSizeOptions = PAGE_SIZE_OPTIONS;


  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator =
    Object.create(null);

  constructor(
    private dialog: MatDialog,
    private _subcategoryService: ClinicalSubCategoryService,
    private _lookupService: LookupService,
    private toastr: ToastrService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private translationUtils: TranslationUtils
  ) { }

  ngOnInit(): void {
    this.categoryId = this.activatedRoute.snapshot.paramMap.get('categoryid');

    this._lookupService.clinicalcategories$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((category: CommonDropDown[]) => {
        this.categoryList = category;
        this.selectedcategory = this.categoryList.find(x => x.id == Number(this.categoryId));
      });

    this._subcategoryService.pagination$
      .pipe(takeUntil(this._unsubscribeAll),)
      .subscribe((pagination: PaginationResult | null) => {
        if (pagination) {
          this.pagination = pagination;
          this.subcategorycount = this.pagination.rowCount;
        }
      });

    this._subcategoryService.subcategories$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.dataSource.data = data;
        this.subcategorydata = data;
      });

    this.searchSubcategory.valueChanges.pipe(
      takeUntil(this._unsubscribeAll),
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((query) => {
        this.isLoading = true;
        return this._subcategoryService.getSubCategory({
          pageNumber: 0,
          pageSize: this.pagination.pageSize,
          isDescending: false,
          searchValue: query || '',
          categoryId: Number(this.categoryId)
        }).pipe(
          finalize(() => (this.isLoading = false))
        );
      })
    ).subscribe((response) => {
      if (response && response.result) {
        this.dataSource.data = response.result.clinicalSubCategories;
        this.subcategorycount = response.result.pagination.rowCount;
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  onAddSubCategory() {
    this.dialog.open(ClinicalSubCategoryDetailsComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
      data: { categoryvalue: this.selectedcategory }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshSubCategories();
      }
    });
  }

  onDeleteSubCategory(id: number) {
    const confirmation = this.dialog.open(DeleteDialog, {
      width: 'auto',
      enterAnimationDuration: '300ms',
      exitAnimationDuration: '300ms',
      disableClose: true,
      data: { message: this.translate.instant('DELETE_DIALOG') + ' ' + this.translate.instant('ENTITY.SUBCATEGORY') + ' ?' }
    })
    confirmation.afterClosed().subscribe((result) => {
      if (result === "confirm") {
        this._subcategoryService.deleteSubcategory(id).subscribe((response) => {
          if (response.isSuccess) {
            this.refreshSubCategories();
            this.toastr.success(this.translationUtils.getTranslatedMessage('COMMON.DELETE', this.translate.instant('ENTITY.SUBCATEGORY')));
          }
          else {
            const allErrors = Object.values(response.errors).flat();
            this.toastr.error(allErrors.join("\n"));
          }
        })
      }
    });
  }

  OnEditSubcategory(data: IClinicalSubCategory): void {

    this.dialog.open(ClinicalSubCategoryDetailsComponent, {
      autoFocus: false,
      width: '700px',
      height: 'auto',
      disableClose: true,
      data: {
        subcategory: data,
        isEdit: true,
      }
    }).afterClosed().subscribe((result) => {
      if (result) {
        this.refreshSubCategories();
      }
    });
  }

  getCategoryName(categoryId: number): string {
    if (!this.categoryList) return 'Unknown'; // Ensure the list exists
    const category = this.categoryList.find(cat => cat.id === categoryId);
    return category ? category.name : 'Unknown';
  }

  refreshSubCategories() {
    const globalSearchValue = this.searchSubcategory?.value?.trim();
    const requestPayload: IClinicalSubCategoryListRequest = {
      pageNumber: 0,
      pageSize: this.pagination?.pageSize || 100,
      isDescending: false,
      categoryId: Number(this.categoryId)
    };

    if (globalSearchValue) {
      requestPayload.searchValue = globalSearchValue;
    } else {
      requestPayload.pageNumber = this.pagination?.currentPage || 0;
    }

    this._subcategoryService.getSubCategory(requestPayload)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        if (response && response.result) {
          const subcategory = response.result.clinicalSubCategories;
          this.subcategorydata = subcategory;
          if (!this.dataSource) {
            this.dataSource = new MatTableDataSource(subcategory);
          } else {
            this.dataSource.data = subcategory;
          }
          this.dataSource.paginator = this.paginator;
          this.subcategorycount = response.result.pagination.rowCount;
          this.pagination = response.result.pagination;
        }
      });
  }

}
